/**
 * Typing `{{` offers the fields the report can actually bind to.
 *
 * The data picker in the bubble menu is the deliberate route; this is the fast
 * one, for people who are already typing a sentence and want a value in the
 * middle of it. Both end in the same token node, so a field inserted either way
 * is the same thing afterwards.
 *
 * The plugin owns only what ProseMirror can know — where the trigger starts and
 * what has been typed since. Which item is highlighted belongs to the popup,
 * which is why the keyboard bindings delegate rather than decide.
 */
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { richTextSchema } from "./schema.js";

/** An open trigger: `{{` plus whatever has been typed after it. */
export interface TokenTrigger {
  /** Document position of the first brace. */
  from: number;
  /** Document position of the cursor. */
  to: number;
  query: string;
}

/** What the popup must be able to do when a key arrives at the editor first. */
export interface AutocompleteHandlers {
  isOpen(): boolean;
  move(delta: number): void;
  /** Returns false to let the key fall through, e.g. nothing is highlighted. */
  accept(): boolean;
  close(): void;
}

export const autocompleteKey = new PluginKey<TokenTrigger | null>("bp-token-autocomplete");

/**
 * A trigger is open when the text between the cursor and the nearest preceding
 * `{{` in the same block contains no brace of its own. Closing the braces by
 * hand, or moving away, ends it.
 */
function triggerAt(state: EditorState): TokenTrigger | null {
  const sel = state.selection;
  if (!sel.empty) return null;
  const $from = sel.$from;
  if (!$from.parent.isTextblock) return null;
  // A replacement character stands in for tokens and other leaf nodes so the
  // offsets stay aligned with the document.
  const before = $from.parent.textBetween(0, $from.parentOffset, undefined, "￼");
  const match = /\{\{([^{}￼]*)$/.exec(before);
  if (!match) return null;
  return {
    from: $from.start() + $from.parentOffset - match[0].length,
    to: sel.from,
    query: match[1]!.trim()
  };
}

export function tokenAutocomplete(opts: { onTrigger(trigger: TokenTrigger | null, view: EditorView): void }): Plugin<TokenTrigger | null> {
  return new Plugin<TokenTrigger | null>({
    key: autocompleteKey,
    state: {
      init: (_config, state) => triggerAt(state),
      apply: (tr, _prev, _old, next) => (tr.getMeta(autocompleteKey) === "close" ? null : triggerAt(next))
    },
    view: (view) => {
      let last: string | null = null;
      const publish = () => {
        const trigger = autocompleteKey.getState(view.state) ?? null;
        // Only tell the popup when something it cares about actually moved.
        const signature = trigger ? `${trigger.from}:${trigger.to}:${trigger.query}` : null;
        if (signature === last) return;
        last = signature;
        opts.onTrigger(trigger, view);
      };
      publish();
      return { update: publish };
    }
  });
}

/** Replaces an open `{{query` with a token node, and a following space. */
export function acceptToken(view: EditorView, trigger: TokenTrigger, expr: string, label: string): void {
  const token = richTextSchema.nodes.token!.create({ expr, label });
  const tr = view.state.tr.replaceWith(trigger.from, trigger.to, token);
  tr.setMeta(autocompleteKey, "close");
  view.dispatch(tr.scrollIntoView());
  view.focus();
}

/** Abandons the trigger, leaving the typed text exactly as it is. */
export function dismissAutocomplete(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(autocompleteKey, "close"));
}

/**
 * Key bindings that apply only while the popup is open, so ordinary typing,
 * Enter and Escape behave normally the rest of the time.
 */
export function autocompleteKeymap(handlers: () => AutocompleteHandlers | null): Record<string, () => boolean> {
  const when = (fn: (h: AutocompleteHandlers) => boolean) => (): boolean => {
    const h = handlers();
    return h && h.isOpen() ? fn(h) : false;
  };
  return {
    ArrowDown: when((h) => (h.move(1), true)),
    ArrowUp: when((h) => (h.move(-1), true)),
    Enter: when((h) => h.accept()),
    Tab: when((h) => h.accept()),
    Escape: when((h) => (h.close(), true))
  };
}
